import React, { useState } from "react";
import { colors, font } from "../utils/theme.js";
import { api } from "../api/client.js";

// Registry-backed client picker. The ONE way clients enter existence is the inline
// "add new" flow here (used by the Ops Planner add-project modal); Proposal Tracker
// reuses it in select-only mode. Enforces name uniqueness server-side: creating an
// existing name returns 409 and we SELECT the existing record instead of duplicating.
//
// Props:
//   value          — selected clientId (or "")
//   onChange(id)   — called with the chosen clientId
//   clients        — registry list [{id, name}]
//   onClientCreated(client) — parent appends a newly created/looked-up client to its list
//   allowCreate    — show the inline "add new" affordance (default true)
//   legacyName     — for a legacy record (no clientId): the old free-text name to show
export default function ClientPicker({ value, onChange, clients = [], onClientCreated, allowCreate = true, legacyName }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLogo, setNewLogo] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const selectStyle = {
    width: "100%", padding: "10px 12px", background: colors.bg, border: `1px solid ${colors.borderLight}`,
    borderRadius: 6, color: colors.text, fontSize: 14, fontFamily: font, outline: "none", boxSizing: "border-box",
  };
  const linkBtn = {
    background: "none", border: "none", color: colors.accent, fontSize: 12, cursor: "pointer",
    fontFamily: font, padding: 0, marginTop: 6,
  };

  const sorted = [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const addClient = async () => {
    const name = newName.trim();
    if (!name || busy) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const res = await api.clients.create(name, newLogo.trim());
      if (res.ok && res.data?.id) {
        onClientCreated?.(res.data);
        onChange(res.data.id);
        setAdding(false); setNewName(""); setNewLogo("");
      } else if (res.status === 409 && res.data?.client) {
        // Name already exists — select it instead of duplicating.
        onClientCreated?.(res.data.client);
        onChange(res.data.client.id);
        setAdding(false); setNewName(""); setNewLogo("");
        setNote(`"${res.data.client.name}" already existed — selected it.`);
      } else {
        setError(res.data?.error || "Could not add client.");
      }
    } catch (e) {
      setError(e?.message || "Could not add client.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {!adding ? (
        <>
          <select style={selectStyle} value={value || ""} onChange={(e) => {
            if (e.target.value === "__new__") { setAdding(true); setError(null); setNote(null); }
            else onChange(e.target.value);
          }}>
            <option value="">Select client</option>
            {sorted.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            {allowCreate && <option value="__new__">+ Add new client…</option>}
          </select>
          {!value && legacyName && (
            <div style={{ fontSize: 12, color: colors.textDim, marginTop: 6 }}>
              Legacy: <span style={{ color: colors.textMuted }}>{legacyName}</span> — pick a client to link it, or leave as-is.
            </div>
          )}
          {note && <div style={{ fontSize: 12, color: colors.success, marginTop: 6 }}>{note}</div>}
        </>
      ) : (
        <div>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              autoFocus
              style={{ ...selectStyle, flex: 1 }}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClient(); } }}
              placeholder="New client name"
            />
            <button onClick={addClient} disabled={busy || !newName.trim()} style={{
              padding: "10px 16px", background: colors.accent, border: "none", borderRadius: 6,
              color: "#fff", fontSize: 14, fontWeight: 600, fontFamily: font,
              cursor: busy || !newName.trim() ? "not-allowed" : "pointer", opacity: busy || !newName.trim() ? 0.5 : 1,
            }}>{busy ? "Adding…" : "Add"}</button>
          </div>
          <input
            style={{ ...selectStyle, marginTop: 8, fontSize: 13 }}
            value={newLogo}
            onChange={(e) => setNewLogo(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addClient(); } }}
            placeholder="Logo image URL (optional)"
          />
          <button style={linkBtn} onClick={() => { setAdding(false); setNewName(""); setNewLogo(""); setError(null); }}>Choose from list instead</button>
          {error && <div style={{ fontSize: 12, color: colors.danger, marginTop: 6 }}>{error}</div>}
        </div>
      )}
    </div>
  );
}
